import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { REPRISE_CHOICES, RepriseBrand } from "@/components/RepriseBrand";
import { toast } from "sonner";
import { t as tr } from "@/lib/i18n";
import { Loader2, ImagePlus, X, Save, Eye, EyeOff } from "lucide-react";

const PLACES = REPRISE_CHOICES.find(([k]) => k === "places")[2];
const FLOORS = REPRISE_CHOICES.find(([k]) => k === "plancher_type")[2];
const TYPES = [["van", "Van chevaux"], ["fourgon", "Remorque fourgon"], ["autre", "Autre"]];
const EMPTY = { type: "", brand: "", model: "", places: "", year: "", plate: "", ptac: "", floor_type: "", warranty_months: "", price_ttc: "", cost_ht: "", vat_recoverable: false, options_text: "", photos: [], location: "", phone: "" };

// Comme pour les reprises : van chevaux Fautras depuis 2002 -> plancher polyéthylène si non renseigné
export const withFloorDefault = (u) => {
  const y = parseInt(String(u.year || "").match(/\d{4}/)?.[0] || "0", 10);
  return u.type === "van" && /^fautras$/i.test((u.brand || "").trim()) && y >= 2002 && !u.floor_type ? { ...u, floor_type: "Polyéthylène" } : u;
};

export const structureWarranty = (u) => {
  const y = parseInt(String(u.year || "").match(/\d{4}/)?.[0] || "0", 10);
  if (!/fautras/i.test(u.brand || "") || !y) return "";
  const left = y + 8 - new Date().getFullYear();
  return left > 0 ? `${tr("Garantie structure Fautras jusqu'en")} ${y + 8} (${left} ${left > 1 ? tr("ans restants") : tr("an restant")})` : "";
};

export const warrantyLine = (u) => [`${tr("Garantie")} ${Math.max(12, Number(u.warranty_months) || 12)} ${tr("mois")}`, structureWarranty(u)].filter(Boolean).join(" · ");

export const usedLabel = (u) => [u.brand, u.model].filter((x) => (x || "").trim()).join(" ").trim() || "Occasion";

export const usedLine = (u) => [tr("Occasion"), tr((TYPES.find(([k]) => k === u.type) || [])[1] || ""), u.year, u.type === "van" && u.places && tr(u.places),
  u.plate && `${tr("Immat.")} ${u.plate}`, u.ptac && `F2 ${u.ptac}`, u.floor_type && `${tr("Plancher")} ${tr(u.floor_type).toLowerCase()}`].filter(Boolean).join(" · ");

// Rebuild used-vehicle lines in the current UI language before printing a poster
export const localizePoster = (poster, used) => (used && poster ? { ...poster, used_line: usedLine(used), warranty_line: warrantyLine(used) } : poster);

// Same price convention as backend used_model(): priceTtcOf() rescales public_ttc from 20 % to the model VAT
export const toUsedModel = (m, u) => {
  const vat = u.vat_recoverable ? 0.2 : 0;
  const price = Number(u.price_ttc) || 0;
  return { ...m, name: usedLabel(u), public_ttc: price * 1.2 / (1 + vat), public_ht: price / (1 + vat), vat_rate: vat, reseller_ht: Number(u.cost_ht) || 0,
    details: u.options_text || "", used: { ...u, price_ttc: price }, image: m.image || u.photos?.[0]?.url || "" };
};

export const USED_DRAFT_ID = "__draft_occasion__";
export const usedDraftModel = (used = {}) => toUsedModel({ id: USED_DRAFT_ID, range_key: "occasion", category: "Occasion", reseller_ht: 0, colors: [], image: "", active: true }, { ...EMPTY, ...used });

export const buildPayload = (u, existing, resellerIds) => {
  const price = Number(u.price_ttc) || 0;
  const label = usedLabel(u);
  const used = { ...u, places: u.type === "van" ? u.places : "", price_ttc: price, cost_ht: Number(u.cost_ht) || 0, warranty_months: Math.max(12, Number(u.warranty_months) || 12) };
  const old = existing?.poster || {};
  const image = old.original_image != null ? old.model_image : (u.photos[0]?.url || "");
  return {
    name: (u.location || "").trim() || "Occasion", range_key: "occasion", range_name: "Occasion",
    model_id: "", model_name: label, country_code: "FR", used, active: true, reseller_ids: resellerIds || existing?.reseller_ids || [],
    poster: {
      salon: (u.location || "").trim(), range_key: "occasion", range_name: "Occasion", model_name: label, model_name_short: label,
      model_image: image, ...(old.original_image != null ? { original_image: old.original_image } : {}), model_details: u.options_text || "", used_line: usedLine(used), warranty_line: warrantyLine(used),
      vat_pct: u.vat_recoverable ? 20 : 0, options: [], model_cat_ttc: price, model_salon_ttc: price,
      total_cat_ttc: price, total_salon_ttc: price, phone: (u.phone || "").trim(), country_code: "FR",
    },
  };
};

export const saveUsed = async (u, existing, resellerIds) => {
  if (!u.type || !u.brand.trim()) { toast.error("Renseignez au moins le type et la marque"); return null; }
  try {
    const payload = buildPayload(u, existing, resellerIds);
    const { data } = existing ? await api.put(`/expos/${existing.id}`, payload) : await api.post("/expos", payload);
    toast.success(existing ? "Occasion mise à jour" : "Occasion mise en stock");
    return data;
  } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de l'enregistrement"); return null; }
};

const Field = ({ label, children, required }) => (
  <div><Label className="text-[11px] text-muted-foreground">{label}{required && <span className="text-destructive"> *</span>}</Label>{children}</div>
);

const TypeLabel = () => <span className="text-[11px] text-muted-foreground">Type <span className="text-destructive">*</span> :</span>;

const CostField = ({ value, onChange }) => {
  const [show, setShow] = useState(false);
  return (
    <Field label="Coût de revient HT (€, masqué)">
      <div className="relative">
        <Input type="text" inputMode="decimal" autoComplete="off" name="used-cost-nofill" data-lpignore="true" data-1p-ignore style={show ? undefined : { WebkitTextSecurity: "disc" }} value={value} onChange={(e) => onChange(e.target.value.replace(",", "."))} className="pr-9" data-testid="used-cost" />
        <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" onClick={() => setShow((s) => !s)} data-testid="used-cost-toggle">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </Field>
  );
};

const UsedVanFields = ({ u, setU: rawSetU, setUploading, uploading }) => {
  const setU = (fn) => rawSetU((x) => withFloorDefault(typeof fn === "function" ? fn(x) : fn));
  const set = (k, v) => setU((x) => ({ ...x, [k]: v }));
  const addPhotos = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    setUploading(true);
    try {
      const added = [];
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", await compressImage(f));
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, name: f.name });
      }
      setU((x) => ({ ...x, photos: [...x.photos, ...added] }));
    } catch (e) { toast.error("Échec de l'envoi des photos"); }
    finally { setUploading(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid="used-type">
        <TypeLabel />
        {TYPES.map(([val, lbl]) => (
          <label key={val} className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid={`used-type-${val}`}>
            <Checkbox checked={u.type === val} onCheckedChange={(v) => { if (v) set("type", val); }} /> {lbl}
          </label>
        ))}
      </div>
      {!u.type && <p className="text-xs text-destructive font-medium -mt-1" data-testid="used-type-required">{tr("Choisissez d'abord le type de véhicule pour remplir le descriptif.")}</p>}
      <fieldset disabled={!u.type} className={`space-y-3 transition-opacity ${u.type ? "" : "opacity-40 pointer-events-none select-none"}`} data-testid="used-fields">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Field label="Marque" required>
          <RepriseBrand type={u.type} value={u.brand} other={!!u.brand_other} testid="used-brand"
            onChange={(p) => setU((x) => ({ ...x, brand: p.marque ?? x.brand, brand_other: p.marqueAutre ?? x.brand_other }))} />
        </Field>
        {u.type === "van" && (
          <Field label="Nombre de places">
            <Select value={u.places || undefined} onValueChange={(v) => set("places", v)}>
              <SelectTrigger data-testid="used-places"><SelectValue placeholder="Choisir" /></SelectTrigger>
              <SelectContent>{PLACES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        )}
        <Field label="Modèle"><Input value={u.model} onChange={(e) => set("model", e.target.value)} data-testid="used-model" /></Field>
        <Field label="Année"><Input inputMode="numeric" value={u.year} onChange={(e) => set("year", e.target.value)} data-testid="used-year" /></Field>
        <Field label="Immatriculation"><Input value={u.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} data-testid="used-plate" /></Field>
        <Field label="F2 (PTAC)"><Input value={u.ptac} onChange={(e) => set("ptac", e.target.value)} data-testid="used-ptac" /></Field>
        <Field label="Type de plancher">
          <Select value={u.floor_type || undefined} onValueChange={(v) => set("floor_type", v)}>
            <SelectTrigger data-testid="used-floor"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>{FLOORS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Garantie (mois, 12 minimum)">
          <Input inputMode="numeric" placeholder="12" value={u.warranty_months} onChange={(e) => set("warranty_months", e.target.value.replace(/\D/g, ""))}
            onBlur={() => { if (u.warranty_months !== "") set("warranty_months", Math.max(12, Number(u.warranty_months) || 12)); }} data-testid="used-warranty" />
        </Field>
        <Field label="Prix de vente TTC (€)"><Input inputMode="decimal" autoComplete="off" name="used-price-nofill" value={u.price_ttc} onChange={(e) => set("price_ttc", e.target.value.replace(",", "."))} data-testid="used-price" /></Field>
        <CostField value={u.cost_ht} onChange={(v) => set("cost_ht", v)} />
        <label className="flex items-center gap-2 text-sm cursor-pointer self-end pb-2" data-testid="used-vat">
          <Checkbox checked={u.vat_recoverable} onCheckedChange={(v) => set("vat_recoverable", !!v)} /> TVA récupérable (TVA 20 %)
        </label>
      </div>
      <p className="text-[11px] text-muted-foreground -mt-1">{u.vat_recoverable ? "Le devis sera établi avec une TVA de 20 % incluse dans le prix." : "Sans TVA récupérable : le devis sera établi sans TVA."}</p>
      {structureWarranty(u) && <p className="text-xs text-[#9E7B41] font-medium -mt-1" data-testid="used-structure-warranty">{structureWarranty(u)}</p>}
      <Field label="Descriptif libre (options, équipements, état… une ligne par élément)">
        <Textarea rows={4} value={u.options_text} onChange={(e) => set("options_text", e.target.value)} data-testid="used-options" />
      </Field>
      <div>
        <Label className="text-[11px] text-muted-foreground">Photos (la première sert de photo principale)</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {u.photos.map((p, i) => (
            <div key={p.url} className="relative">
              <img src={p.url} alt={`Photo ${i + 1}`} className="w-20 h-16 object-cover rounded border border-border" />
              <button type="button" onClick={() => setU((x) => ({ ...x, photos: x.photos.filter((y) => y.url !== p.url) }))}
                className="absolute -top-1.5 -right-1.5 bg-background border border-border rounded-full p-0.5" data-testid={`used-photo-remove-${i}`}><X className="w-3 h-3" /></button>
            </div>
          ))}
          <label className="w-20 h-16 rounded border border-dashed border-border flex items-center justify-center cursor-pointer text-muted-foreground hover:text-foreground" data-testid="used-photo-add">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
            <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} data-testid="used-photo-input" />
          </label>
        </div>
      </div>
      </fieldset>
    </div>
  );
};

// Inline editor in the configurator "Descriptif" card: edits update the quote live, "Enregistrer" saves the stock record
export const UsedVanInline = ({ existing, initial, onChange, onSaved, showSave = true }) => {
  const [u, setU] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setU({ ...EMPTY, ...(existing?.used || initial || {}) });
    setDirty(false);
  }, [existing?.id, initial]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (fn) => { setU(fn); setDirty(true); };
  useEffect(() => { if (dirty) onChange?.(u); }, [u]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    const data = await saveUsed(u, existing);
    setBusy(false);
    if (data) { setDirty(false); onSaved?.(data); }
  };
  return (
    <div className="space-y-3" data-testid="used-inline">
      <UsedVanFields u={u} setU={update} uploading={uploading} setUploading={setUploading} />
      {showSave && <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-[11px] text-amber-600" data-testid="used-unsaved">Modifications non enregistrées dans le stock</span>}
        <Button onClick={save} disabled={busy || uploading || (existing && !dirty)} className="gap-1.5" data-testid="used-inline-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {existing ? "Enregistrer" : "Mettre en stock"}
        </Button>
      </div>}
    </div>
  );
};

export const UsedVanDialog = ({ open, onOpenChange, initial, existing, onSaved }) => {
  const [u, setU] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    if (open) setU({ ...EMPTY, ...(existing?.used || initial || {}), location: existing?.used?.location ?? initial?.location ?? "" });
  }, [open, existing, initial]);
  const save = async () => {
    setBusy(true);
    const data = await saveUsed(u, existing);
    setBusy(false);
    if (data) { onOpenChange(false); onSaved?.(data); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="used-van-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? "Modifier l'occasion" : "Mettre en stock une occasion"}</DialogTitle>
          <DialogDescription>Le véhicule apparaîtra dans « Véhicules à la vente » avec le badge Occasion et dans l'onglet « Occasion » du configurateur.</DialogDescription>
        </DialogHeader>
        <UsedVanFields u={u} setU={setU} uploading={uploading} setUploading={setUploading} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="used-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy || uploading} data-testid="used-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}{existing ? "Enregistrer" : "Mettre en stock"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
